import {
  useEffect,
  useRef,
  useState,
} from "react";
import { FaSearch } from "react-icons/fa";
import {
  Link,
  useNavigate,
} from "react-router-dom";

import {
  searchPosts,
} from "../services/api";
import { getPublicPostTitle } from "../utils/postTitle";

import "../styles/searchBar.css";

const SearchBar = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] =
    useState([]);
  const [loading, setLoading] =
    useState(false);
  const [searched, setSearched] =
    useState(false);
  const [
    selectedIndex,
    setSelectedIndex,
  ] = useState(-1);

  const searchRef = useRef(null);
  const navigate = useNavigate();

  const cleanQuery = query.trim();

  const closeSearch = ({
    clearQuery = true,
  } = {}) => {
    if (clearQuery) {
      setQuery("");
    }

    setResults([]);
    setLoading(false);
    setSearched(false);
    setSelectedIndex(-1);
  };

  const openSearchPage = () => {
    if (cleanQuery.length < 2) return;

    navigate(
      `/search?q=${encodeURIComponent(
        cleanQuery,
      )}`,
    );

    closeSearch();
  };

  useEffect(() => {
    if (cleanQuery.length < 2) {
      setResults([]);
      setLoading(false);
      setSearched(false);
      setSelectedIndex(-1);
      return;
    }

    let active = true;
    const controller =
      new AbortController();

    const timer = window.setTimeout(
      async () => {
        try {
          setLoading(true);
          setSearched(false);

          const response =
            await searchPosts(
              cleanQuery,
              {
                limit: 6,
                includeSnippet: false,
                signal:
                  controller.signal,
              },
            );

          if (!active) return;

          setResults(
            Array.isArray(response.data)
              ? response.data
              : [],
          );

          setSearched(true);
          setSelectedIndex(-1);
        } catch (error) {
          if (
            error?.code ===
              "ERR_CANCELED" ||
            controller.signal
              .aborted
          ) {
            return;
          }

          console.error(
            "Search suggestions failed:",
            error,
          );

          if (!active) return;

          setResults([]);
          setSearched(true);
        } finally {
          if (active) {
            setLoading(false);
          }
        }
      },
      350,
    );

    return () => {
      active = false;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [cleanQuery]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(
          event.target,
        )
      ) {
        closeSearch({
          clearQuery: false,
        });
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick,
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick,
      );
    };
  }, []);

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      closeSearch();
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();

      if (
        selectedIndex >= 0 &&
        results[selectedIndex]?.slug
      ) {
        navigate(
          `/post/${results[selectedIndex].slug}`,
        );

        closeSearch();
        return;
      }

      openSearchPage();
      return;
    }

    if (!results.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();

      setSelectedIndex((current) =>
        current < results.length - 1
          ? current + 1
          : 0,
      );
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();

      setSelectedIndex((current) =>
        current > 0
          ? current - 1
          : results.length - 1,
      );
    }
  };

  return (
    <div
      className="search-container"
      ref={searchRef}
    >
      <div className="search-box">
        <input
          className="search-input"
          type="search"
          value={query}
          placeholder="Search jobs, results..."
          aria-label="Search posts"
          autoComplete="off"
          maxLength={100}
          onChange={(event) => {
            setQuery(event.target.value);
            setSelectedIndex(-1);
          }}
          onKeyDown={handleKeyDown}
        />

        <button
          className="search-button"
          type="button"
          aria-label="Submit search"
          disabled={cleanQuery.length < 2}
          onClick={openSearchPage}
        >
          <FaSearch />
        </button>

        {(loading ||
          searched ||
          results.length > 0) && (
          <div
            className="search-results"
            role="listbox"
          >
            {loading && (
              <div className="search-loading">
                <span className="loader" />
                Searching articles...
              </div>
            )}

            {searched &&
              !loading &&
              results.length === 0 && (
                <div className="search-empty">
                  <div className="empty-icon">
                    🔍
                  </div>

                  <h4>No articles found</h4>

                  <p>
                    Try another keyword.
                  </p>
                </div>
              )}

            {!loading &&
              results.map(
                (post, index) => (
                  <Link
                    key={
                      post._id ||
                      post.slug
                    }
                    to={`/post/${post.slug}`}
                    role="option"
                    aria-selected={
                      selectedIndex === index
                    }
                    className={
                      selectedIndex === index
                        ? "search-item active"
                        : "search-item"
                    }
                    onClick={() =>
                      closeSearch()
                    }
                  >
                    <div className="search-top">
                      <span className="search-category">
                        {post.category ||
                          "Uncategorized"}
                      </span>

                      <span className="search-date">
                        {post.publishDate
                          ? new Date(
                              post.publishDate,
                            ).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                              },
                            )
                          : ""}
                      </span>
                    </div>

                    <div className="search-title">
                      {getPublicPostTitle(post, { fallback: "Untitled Post" })}
                    </div>
                  </Link>
                ),
              )}

            {results.length > 0 && (
              <button
                type="button"
                className="search-footer"
                onClick={openSearchPage}
              >
                View all results for “
                {cleanQuery}”
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBar;